import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

import { SITE } from "../config";
import { wordmarkSvg } from "./logo";

export function initSmoothScroll(reduced: boolean): Lenis | null {
  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });
  if (reduced) return null;
  const lenis = new Lenis({ autoRaf: false, anchors: true, allowNestedScroll: true });
  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
  const resync = () => {
    lenis.resize();
    ScrollTrigger.refresh();
  };
  window.addEventListener("load", resync);
  void document.fonts?.ready.then(resync);
  window.addEventListener("otridon:lock", () => lenis.stop());
  window.addEventListener("otridon:unlock", () => lenis.start());
  return lenis;
}

export function initLineReveal(): void {
  if (!window.matchMedia("(min-width: 992px) and (hover: hover) and (pointer: fine)").matches)
    return;
  const wrap = (el: Element | null) => {
    if (!el || el.querySelector(".line-inner")) return;
    const text = (el.textContent ?? "").trim();
    if (!text) return;
    const inner = document.createElement("span");
    inner.className = "line-inner";
    inner.dataset.text = text;
    inner.textContent = text;
    el.replaceChildren(inner);
  };
  document.querySelectorAll("[data-line-reveal] > span").forEach(wrap);
  document.querySelectorAll("[data-line-reveal-text]").forEach(wrap);
}

function splitLines(el: HTMLElement): HTMLElement[] {
  const parts = el.innerHTML
    .split(/<br\s*\/?>/i)
    .map((s) => s.trim())
    .filter(Boolean);
  el.innerHTML = parts.map((p) => `<span class="line"><span>${p}</span></span>`).join("");
  return Array.from(el.querySelectorAll<HTMLElement>(".line > span"));
}

export function initReveals(reduced: boolean): gsap.core.Timeline {
  const intro = gsap.timeline({ paused: true });
  document.querySelectorAll<HTMLElement>('[data-reveal="lines"]').forEach((el) => {
    const lines = splitLines(el);
    if (reduced) return;
    if (el.dataset.revealOn === "load") {
      intro.from(lines, { yPercent: 105, duration: 1.25, ease: "expo.out", stagger: 0.09 }, 0.15);
      return;
    }
    gsap.from(lines, {
      yPercent: 105,
      duration: 1.2,
      ease: "expo.out",
      stagger: 0.08,
      scrollTrigger: { trigger: el, start: "top 88%", once: true },
    });
  });
  if (!reduced) {
    gsap.utils.toArray<HTMLElement>('[data-reveal="fade"]').forEach((el) => {
      gsap.from(el, {
        y: 24,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 90%", once: true },
      });
    });
    intro.from(
      "[data-fade-on='load']",
      { y: 18, opacity: 0, duration: 1, ease: "power3.out", stagger: 0.1 },
      0.55,
    );
    intro.from(
      ".header__inner > :not(.logo)",
      { y: -16, opacity: 0, duration: 0.9, ease: "power3.out", stagger: 0.08 },
      0.3,
    );
    gsap.utils.toArray<HTMLElement>(".step").forEach((el, i) => {
      gsap.from(el, {
        y: 50,
        opacity: 0,
        duration: 1.1,
        ease: "power3.out",
        delay: i * 0.08,
        scrollTrigger: { trigger: el, start: "top 92%", once: true },
      });
    });
  }
  return intro;
}

export function initHeader(): void {
  const header = document.querySelector<HTMLElement>("[data-header]");
  if (!header) return;
  const heroName = document.querySelector<HTMLElement>("[data-whirl]");
  if (heroName) {
    new IntersectionObserver(
      (entries) => header.classList.toggle("has-logo", !(entries.at(-1)?.isIntersecting ?? false)),
      { rootMargin: "-70px 0px 0px 0px", threshold: 0 },
    ).observe(heroName);
  } else {
    header.classList.add("has-logo");
  }
  const chapters = Array.from(document.querySelectorAll<HTMLElement>("[data-section]"));
  const half = (el: Element | null | undefined) =>
    (el?.querySelector<HTMLElement>(":scope > .wave-band")?.offsetHeight ?? 0) / 2;
  const sync = () => {
    const y = 40;
    const over = chapters.find((el) => {
      const r = el.getBoundingClientRect();
      return r.top - half(el) <= y && r.bottom - half(el.nextElementSibling) > y;
    });
    header.classList.toggle("is-dark", over?.dataset.section === "dark");
    header.classList.toggle("is-light", over?.dataset.section === "light");
  };
  window.addEventListener("scroll", sync, { passive: true });
  window.addEventListener("resize", sync);
  sync();

  const nav = header.querySelector<HTMLElement>("[data-nav]");
  const links = Array.from(header.querySelectorAll<HTMLAnchorElement>(".nav__link[href^='#']"));
  const bar = document.createElement("span");
  bar.className = "nav__bar";
  bar.setAttribute("aria-hidden", "true");
  nav?.append(bar);
  const placeBar = () => {
    const current = links.find((a) => a.classList.contains("is-current"));
    if (!current) return;
    bar.style.width = `${current.offsetWidth}px`;
    bar.style.transform = `translateX(${current.offsetLeft}px)`;
  };
  window.addEventListener("resize", placeBar);
  void document.fonts?.ready.then(placeBar);
  placeBar();
  const covers = (a: HTMLAnchorElement) =>
    (a.dataset.spy ?? a.getAttribute("href") ?? "").split(/\s+/).filter(Boolean);
  const sections = links
    .flatMap(covers)
    .map((sel) => document.querySelector<HTMLElement>(sel))
    .filter((s): s is HTMLElement => Boolean(s));
  const spy = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const id = `#${e.target.id}`;
        links.forEach((a) => a.classList.toggle("is-current", covers(a).includes(id)));
        placeBar();
      }
    },
    { rootMargin: "-45% 0px -50% 0px" },
  );
  sections.forEach((s) => spy.observe(s));

  const burger = header.querySelector<HTMLButtonElement>("[data-burger]");
  const menu = header.querySelector<HTMLElement>("[data-mobile-menu]");
  if (!burger || !menu) return;
  const setOpen = (open: boolean) => {
    burger.setAttribute("aria-expanded", String(open));
    menu.hidden = !open;
    document.documentElement.style.overflow = open ? "hidden" : "";
  };
  burger.addEventListener("click", () => setOpen(burger.getAttribute("aria-expanded") !== "true"));
  menu.querySelectorAll("a").forEach((a) => a.addEventListener("click", () => setOpen(false)));
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setOpen(false);
  });
}

export function initBrand(): void {
  document.querySelectorAll("[data-brand]").forEach((el) => {
    el.innerHTML = `${wordmarkSvg()}<span class="sr-only">${SITE.name.toUpperCase()}</span>`;
  });
  document.querySelectorAll("[data-brand-name]").forEach((el) => (el.textContent = SITE.name));
  document.querySelectorAll<HTMLButtonElement>("[data-email]").forEach((button) =>
    button.addEventListener("click", () => {
      const address = SITE.email();
      button.textContent = address;
      button.classList.add("footer__plain");
      window.location.href = `mailto:${address}`;
    }),
  );
  const hint = () => document.documentElement.classList.toggle("has-scrolled", window.scrollY > 40);
  window.addEventListener("scroll", hint, { passive: true });
  hint();
}
